import React, { Component, PureComponent, ReactNode } from 'react';
import { Flex } from '../../../common/Flex';
import {
    FlightId,
    HotspotDto,
    MonitoredLoadValue,
    MonitoredOccupancyLoadValues,
    TimePeriod,
    TvRefLocation,
} from 'app/model/CORE';
import MonitoredLoadUtils from '../../../../common/util/MonitoredLoadUtils';
import { EntryLoadBar } from './svg/EntryLoadBar';
import { MonitoredEvent, MonitoredMcdmMeasure, MonitoredMeasure, TvMeteoAlert } from 'app/model/SOL49';
import { MeasureLoadBar } from './svg/MeasureLoadBar';
import { OccupancyLoadBar } from './svg/OccupancyLoadBar';
import { CheckCircleFilled, FileFilled, MoreOutlined, StarFilled } from '@ant-design/icons';
import { PopoverMenu } from '../../../common/PopoverMenu';
import TimePeriodUtils from '../../../../common/util/TimePeriodUtils';
import memoizeOne from 'memoize-one';
import MeasureUtils from '../../../../common/util/MeasureUtils';
import { RegulationPeriods } from 'app/model/client/CORE';
import './TVLoad.less';
import { ModalHandler } from '../../../panels/ModalHandler';
import { DCBSolverUtils } from 'app/common/util/DCBSolverUtils';
import { MeteoLoadBar } from './svg/MeteoLoadBar';
import { OpenMcdmMeasure, OpenTvMonitoredEvents, ShowModalEvent } from 'app/common/eventbus/Events';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { MenuUtils } from 'app/components/dashboard/common/MenuUtils';
import ConditionalRender from 'app/common/components/ConditionalRender';
import Utils from 'app/common/util/Utils';
import { MonitoredEventType, MonitoredEventUtils } from 'app/common/util/MonitoredEventUtils';
import { HotspotBar } from 'app/components/dashboard/network/flows/svg/HotspotBar';
import { HotspotPanel } from 'app/components/panels/measure/HotspotPanel';
import TimeUtils from 'app/common/util/TimeUtils';
import { ApplicableScenarioMenuUtils, ScenarioMenuState } from '../../common/ApplicableScenarioMenuUtils';
import { RootState } from '../../../../redux/store';

import {
    addToFlowSection,
    createFlowSection,
    removeFromFlowSection,
    selectAllFlows,
    selectUserCustomFlows,
} from '../../../../redux/features/dashboard/user-monitored-flows-slice';
import { connect, ConnectedProps, shallowEqual } from 'react-redux';

const tvMoreMenuMapStateToProps = (state: RootState) => {
    return {
        allFlowsSections: selectAllFlows(state),
        customFlowSections: selectUserCustomFlows(state),
    };
};
const tvMoreMenuMapMapDispatchToProps = {
    removeFromFlowSection,
    createFlowSection,
    addToFlowSection,
};

const tvMoreMenuComponentConnector = connect(tvMoreMenuMapStateToProps, tvMoreMenuMapMapDispatchToProps);
type TvMoreMenuPropsFromRedux = ConnectedProps<typeof tvMoreMenuComponentConnector>;

interface TvMoreMenuProps extends TvMoreMenuPropsFromRedux {
    tvId: string;
    refLoc?: TvRefLocation;
    flowUUID: number;
    vsName: string;
    regulationPeriods: Array<RegulationPeriods>;
    occupancyPeriods: Map<FlightId, TimePeriod>;
    measures: string[];

    findSolutionDisabled?: () => boolean;
    pendingMeasureProposal?: MonitoredMcdmMeasure;
    events?: MonitoredEvent[];
    hotspots: HotspotDto[];
}

interface TvMoreMenuState extends ScenarioMenuState {}
class TvMoreMenuComponent extends Component<TvMoreMenuProps, TvMoreMenuState> {
    constructor(props: TvMoreMenuProps) {
        super(props);
        this.state = {
            isApplicableScenarioLoading: false,
        };
    }

    handleMoreMenuItemClick = (key: string) => {
        switch (key) {
            case 'find-solution':
                ModalHandler.showFindSolution([{ tvId: this.props.tvId }]);
                break;
            case 'create-regulation':
                ModalHandler.showRegulationEditor(this.props.tvId, 'tv');
                break;
            case 'hotspot':
                ShowModalEvent.fireNotClosable(
                    <HotspotPanel tv={this.props.tvId} refLoc={this.props.refLoc} />,
                    HotspotPanel.HOTSPOT_PANEL_WIDTH_EM,
                );
                break;
            case 'show-details':
                ModalHandler.showTvDetails(
                    this.props.tvId,
                    this.props.vsName,
                    this.props.regulationPeriods,
                    this.props.occupancyPeriods,
                );
                break;
            case 'open-proposal':
                OpenMcdmMeasure.fireEvent([this.props.pendingMeasureProposal]);
                break;
            case 'remove-from-section':
                const flow = this.props.allFlowsSections.find(
                    flow => !flow.predefined && flow.uuid === this.props.flowUUID,
                );
                this.props.removeFromFlowSection({ flow: flow, tvsToRemove: [this.props.tvId] });
                break;
            case 'notes':
                OpenTvMonitoredEvents.fire(
                    MonitoredEventType.NOTE,
                    this.props.tvId,
                    this.props.refLoc,
                    [this.props.vsName],
                    this.props.measures,
                );
                break;
            case 'events':
                OpenTvMonitoredEvents.fire(
                    MonitoredEventType.WEATHER_EVENT,
                    this.props.tvId,
                    this.props.refLoc,
                    [this.props.vsName],
                    this.props.measures,
                );
                break;
        }
    };

    getMenuItems = (): ItemType[] => {
        const isInCustomSection = this.props.allFlowsSections.some(
            flow => !flow.predefined && flow.uuid === this.props.flowUUID,
        );
        // todo remove this comment and works
        const menuItems: ItemType[] = [{ key: 'show-details', label: 'Show details (graphs)' }];

        if (this.props.pendingMeasureProposal) {
            menuItems.push({
                key: 'open-proposal',
                label: `Open measure proposal ${this.props.pendingMeasureProposal.measure.measureId.id}`,
            });
        } else {
            menuItems.push(
                { key: 'find-solution', label: 'Find solution', disabled: this.props.findSolutionDisabled?.() },
                { key: 'create-regulation', label: 'Create a new regulation' },
                {
                    key: 'hotspot',
                    label: `${Utils.isEmpty(this.props.hotspots) ? 'Create a new hotspot' : 'Edit hotspot'}`,
                },
                ApplicableScenarioMenuUtils.applicableScenarioMenu(
                    [this.props.tvId],
                    null,
                    this.state,
                    (state: TvMoreMenuState) => this.setState(state),
                ),
            );
        }
        menuItems.push(
            { key: 'divider-1', type: 'divider' },
            MenuUtils.addToCustomSectionMenu(
                this.props.customFlowSections,
                payload => this.props.addToFlowSection(payload),
                payload => this.props.createFlowSection(payload),
                [this.props.tvId],
            ),
        );

        if (isInCustomSection) {
            menuItems.push({ key: 'remove-from-section', label: 'Remove from custom section' });
        }
        menuItems.push({ key: 'show-on-map', label: 'Show on the map', disabled: true });

        const notes = this.props.events?.filter(MonitoredEventUtils.isNoteEvent);
        const events = this.props.events?.filter(MonitoredEventUtils.isWeatherEvent);

        menuItems.push(
            { key: 'divider-2', type: 'divider' },
            { key: 'notes', label: `${Utils.isNotEmpty(notes) ? 'Show notes' : 'Add a note'}` },
            { key: 'events', label: `${Utils.isNotEmpty(events) ? 'Show events' : 'Add an event'}` },
        );
        return menuItems;
    };

    render() {
        return (
            <PopoverMenu onMenuItemClick={this.handleMoreMenuItemClick} menuItems={this.getMenuItems}>
                <MoreOutlined />
            </PopoverMenu>
        );
    }
}

const MemoizedComponent = React.memo(TvMoreMenuComponent, (prevProps, nextProps) => {
    return shallowEqual(prevProps, nextProps);
});

const ConnectedTvMoreMenu = tvMoreMenuComponentConnector(MemoizedComponent);

export interface TVLoadProps {
    tvName?: string;
    refLoc?: TvRefLocation;
    flowUUID?: number;
    vsName?: string;
    elementary: boolean;
    simulated?: boolean;
    entryLoad: Array<MonitoredLoadValue>;
    occupancyLoad?: Array<MonitoredOccupancyLoadValues>;
    currentTime: Date;
    monitoredMeasures?: Array<MonitoredMeasure>;
    activePeriods?: Array<TimePeriod>;
    showLoadInactivePeriods?: boolean;
    occupancyPeriods?: Map<FlightId, TimePeriod>;
    monitorable?: boolean;
    hideMoreMenu?: boolean;
    moreMenu?: ReactNode; // Override default more menu
    hideTvName?: boolean; //hides tv name label
    hideVsName?: boolean; //hides tv set name label
    hideSelectIcon?: boolean; //hides selection icon

    canSelectPeriod?: boolean;
    defaultSelectedPeriod?: TimePeriod;
    onSelectedPeriodChange?(selectedPeriod: TimePeriod): void;

    selected?: boolean;
    onSelectionChange?(selected: boolean): void;

    displayOccupancy?: 'all' | 'sustain' | 'peak' | 'none'; // default is 'all'
    displayOccupancyForTv?: 'all' | 'low' | 'high' | 'overload'; // default is 'all'

    //meteo alerts
    tvMeteoAlerts?: Array<TvMeteoAlert>;

    //concerned by ongoing measure proposal
    pendingMeasureProposal?: MonitoredMcdmMeasure;
    events?: MonitoredEvent[];
    hotspots?: HotspotDto[];
}

interface TVLoadState {
    selectedPeriod: TimePeriod;
    animated: boolean;
}

export class TVLoad extends PureComponent<TVLoadProps, TVLoadState> {
    constructor(props) {
        super(props);
        this.state = {
            selectedPeriod: props.defaultSelectedPeriod,
            animated: false,
        };
    }

    componentDidMount() {
        if (Utils.isExists(this.props.pendingMeasureProposal)) {
            this.hasBeenAnimated();
        }
    }

    componentDidUpdate(prevProps, prevState): void {
        if (this.props.defaultSelectedPeriod !== prevProps.defaultSelectedPeriod) {
            this.setState({ selectedPeriod: this.props.defaultSelectedPeriod });
        }

        if (Utils.isExists(this.props.pendingMeasureProposal) && !prevState.animated) {
            this.hasBeenAnimated();
        } else if (Utils.isNotExists(this.props.pendingMeasureProposal) && prevState.animated) {
            // No more pending proposal, reset animation state in order to animate it "again" when new proposal arrives
            this.setState({
                animated: false,
            });
        }
    }

    private hasBeenAnimated = () => {
        // prevent animation from restarting ! Adding delay to let current animation finish
        setTimeout(
            () =>
                this.setState({
                    animated: true,
                }),
            15000,
        );
    };

    handleSelectionChange = () => {
        // If selected is undefined, it means that the component cannot be selected
        this.props.selected !== undefined && this.props.onSelectionChange?.(!this.props.selected);
    };

    /**
     * Extract monitored period from load values
     */
    getMonitoredPeriod = memoizeOne((loadValues: Array<MonitoredLoadValue>) => {
        return MonitoredLoadUtils.getMonitoredPeriod(loadValues);
    });

    getTvRegulationPeriods = memoizeOne((monitoredMeasures: Array<MonitoredMeasure>): Array<RegulationPeriods> => {
        // The reg periods can't be empty, otherwise CountGraph won't display
        if (Utils.isEmpty(monitoredMeasures)) return;

        return monitoredMeasures.map(monitoredMeasure => MeasureUtils.getRegulationPeriods(monitoredMeasure.measure));
    });

    private getMeasureIds = memoizeOne((measures: MonitoredMeasure[]): string[] =>
        measures?.map(m => m.measure.measureId.id),
    );

    /**
     * Merge overlapping occupancy periods together
     */
    getMergedOccupancyPeriods = () => {
        const occupancyPeriods = this.props.occupancyPeriods && Array.from(this.props.occupancyPeriods.values());
        return occupancyPeriods && TimePeriodUtils.sortAndCollapse(occupancyPeriods);
    };

    handleSelectedPeriodChange = (selectedPeriod: TimePeriod) => {
        this.setState({ selectedPeriod: selectedPeriod }, () => this.props.onSelectedPeriodChange?.(selectedPeriod));
    };

    hasNoActivePeriodAfterCurrentTime = () => {
        // adding 30 minutes to current time as it will be the amount of minutes added to compute the getAutoTvSelection (SolverPanel.getTvLoadAndPeriods)
        // because if the TV is currently active but won't be in 30 minutes then it will fail getting the auto selection period (null)
        return !DCBSolverUtils.hasActivePeriodAfter(
            TimeUtils.addMinutes(TimeUtils.withoutSeconds(this.props.currentTime), 30),
            this.props.activePeriods,
        );
    };

    renderMoreMenu = (measures: string[]) => {
        if (this.props.hideMoreMenu) return null;

        const selectorClassName = this.props.selected !== undefined ? 'show-on-hover' : 'hide';

        return (
            <div className={'more-button ' + selectorClassName} onClick={e => e.stopPropagation()}>
                {this.props.moreMenu || (
                    <ConnectedTvMoreMenu
                        tvId={this.props.tvName}
                        refLoc={this.props.refLoc}
                        flowUUID={this.props.flowUUID}
                        vsName={this.props.vsName}
                        regulationPeriods={this.getTvRegulationPeriods(this.props.monitoredMeasures)}
                        occupancyPeriods={this.props.occupancyPeriods}
                        findSolutionDisabled={this.hasNoActivePeriodAfterCurrentTime}
                        pendingMeasureProposal={this.props.pendingMeasureProposal}
                        events={this.props.events}
                        hotspots={this.props.hotspots}
                        measures={measures}
                    />
                )}
            </div>
        );
    };

    handleOpenEvent = (event: React.MouseEvent, measures: string[]) => {
        event.stopPropagation();
        OpenTvMonitoredEvents.fire(
            MonitoredEventUtils.getType(this.props.events?.[0]),
            this.props.tvName,
            this.props.refLoc,
            [this.props.vsName],
            measures,
        );
    };

    render() {
        const monitoredPeriod = this.getMonitoredPeriod(this.props.entryLoad);
        const className =
            'tv-loadbar on-hover' +
            (this.props.selected ? ' selected' : '') +
            (!this.props.monitorable ? ' non-monitorable' : '') +
            (this.props.simulated ? ' simulated' : '') +
            (this.props.pendingMeasureProposal && !this.state.animated ? ' blink-new-proposal' : '');
        const selectorClassName = this.props.selected !== undefined ? 'show-on-hover' : 'hide';
        const occupancyPeriods = this.getMergedOccupancyPeriods(); // Merge overlapping periods
        const measureIds = this.getMeasureIds(this.props.monitoredMeasures);
        return (
            <Flex className={className} onClick={this.handleSelectionChange}>
                <ConditionalRender condition={!this.props.hideSelectIcon}>
                    <div className={'check-circle ' + selectorClassName} style={{ padding: '0 0.25em' }}>
                        <CheckCircleFilled />
                    </div>
                    <Flex direction="vertical" size="0.6em">
                        <div style={{ color: '#A3A3A3', fontSize: '1.25em' }}>
                            {this.props.elementary ? <span>&bull;</span> : undefined}
                        </div>
                    </Flex>
                </ConditionalRender>
                <ConditionalRender
                    condition={
                        !this.props.hideTvName ||
                        !this.props.hideVsName ||
                        this.props.pendingMeasureProposal !== undefined
                    }>
                    <Flex direction="vertical" size="5em">
                        <ConditionalRender condition={!this.props.hideTvName}>
                            <div style={{ color: '#A3A3A3', fontWeight: 'bold', fontSize: '0.85em' }}>
                                {this.props.tvName}
                            </div>
                        </ConditionalRender>
                        <Flex>
                            <ConditionalRender condition={!this.props.hideVsName}>
                                <div style={{ color: '#8A8A8A', fontWeight: 'bold', fontSize: '0.85em' }}>
                                    {this.props.vsName}
                                </div>
                            </ConditionalRender>
                            <ConditionalRender condition={this.props.pendingMeasureProposal !== undefined}>
                                <StarFilled style={{ color: 'lightblue', alignSelf: 'flex-start' }} />
                            </ConditionalRender>
                        </Flex>
                        <ConditionalRender condition={Utils.isNotEmpty(this.props.events)}>
                            <FileFilled
                                style={{ alignSelf: 'flex-start' }}
                                onClick={event => this.handleOpenEvent(event, measureIds)}
                            />
                        </ConditionalRender>
                    </Flex>
                </ConditionalRender>
                <Flex direction="vertical">
                    <EntryLoadBar
                        drawBottomLine={true}
                        loadValues={this.props.entryLoad}
                        currentTime={this.props.currentTime}
                        monitoredPeriod={monitoredPeriod}
                        activePeriods={this.props.activePeriods}
                        showLoadInactivePeriods={this.props.showLoadInactivePeriods}
                        occupancyPeriods={occupancyPeriods}
                        canSelectPeriod={this.props.canSelectPeriod}
                        selectedPeriod={this.state?.selectedPeriod}
                        onSelectedPeriodChange={this.handleSelectedPeriodChange}
                        barHeightEm={1}
                    />
                    {this.props.occupancyLoad
                        ?.filter(
                            occupancyValues =>
                                // Display occupancy if highest load is above defined value (displayOccupancy)
                                // and highest entry load is above another value (displayOccupancyForTv)
                                MonitoredLoadUtils.isOccupancyDisplayed(
                                    occupancyValues.occupancyLoadValues,
                                    this.props.displayOccupancy,
                                ) &&
                                this.props.entryLoad &&
                                MonitoredLoadUtils.isEntryDisplayed(
                                    this.props.entryLoad,
                                    this.props.displayOccupancyForTv,
                                ),
                        )
                        .map(occupancyValues => {
                            return (
                                <OccupancyLoadBar
                                    key={'occupancy-' + occupancyValues.durationInMin}
                                    drawBottomLine={true}
                                    loadValues={occupancyValues.occupancyLoadValues}
                                    activePeriods={this.props.activePeriods}
                                    showLoadInactivePeriods={this.props.showLoadInactivePeriods}
                                    currentTime={this.props.currentTime}
                                    monitoredPeriod={monitoredPeriod}
                                    occupancyPeriods={occupancyPeriods}
                                    canSelectPeriod={this.props.canSelectPeriod}
                                    selectedPeriod={this.state?.selectedPeriod}
                                    onSelectedPeriodChange={this.handleSelectedPeriodChange}
                                    barHeightEm={1}
                                />
                            );
                        })}
                    {this.props.monitoredMeasures?.map(measure => {
                        if (this.props.tvName) {
                            // Display detailed load only if TVset is not colapsed (tvName is provided)
                            return (
                                <MeasureLoadBar
                                    key={'measure-load-' + measure.measure.measureId.id}
                                    loadValues={measure.measureLoad}
                                    monitoredPeriod={monitoredPeriod}
                                    currentTime={this.props.currentTime}
                                    barHeightEm={0.5}
                                />
                            );
                        } else {
                            return (
                                <MeasureLoadBar
                                    key={'measure-load-' + measure.measure.measureId.id}
                                    loadValues={measure.measureLoad}
                                    monitoredPeriod={monitoredPeriod}
                                    currentTime={this.props.currentTime}
                                    barHeightEm={0.1}
                                    fillColor="white"
                                />
                            );
                        }
                    })}
                    {
                        <MeteoLoadBar
                            currentTime={this.props.currentTime}
                            loadValues={MonitoredLoadUtils.getTvMeteoAlertsLoadValues(
                                this.props.tvMeteoAlerts,
                                monitoredPeriod,
                            )}
                            monitoredPeriod={monitoredPeriod}
                            barHeightEm={0.5}
                        />
                    }
                    {this.props.hotspots?.map((hotspot, idx) => {
                        return (
                            <HotspotBar
                                key={`hotspot-bar-${idx}`}
                                hotspot={hotspot}
                                monitoredPeriod={monitoredPeriod}
                                barHeight={0.7}
                            />
                        );
                    })}
                </Flex>
                {this.renderMoreMenu(measureIds)}
            </Flex>
        );
    }
}
